import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { torcidaApi } from './torcidaApi';
import './torcida.css';

export default function TorcidaRanking() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [dados, setDados] = useState(null);
  const [escopo, setEscopo] = useState('loja'); // 'loja' | 'global' (só funcionário)
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Quem está logado define o ranking: cliente vê só clientes, funcionário vê funcionários (loja + geral).
  const ehFuncionario = useMemo(() => {
    try { return JSON.parse(localStorage.getItem(`torc_part_${slug}`))?.eh_funcionario === true; }
    catch { return false; }
  }, [slug]);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = (ehFuncionario && escopo === 'global')
        ? await torcidaApi.rankingGlobalFunc()
        : await torcidaApi.ranking(slug, ehFuncionario ? 'funcionario' : null);
      setDados(res.data);
      setErro('');
    } catch (e) {
      setErro(e.response?.data?.error || 'Não foi possível carregar o ranking.');
    } finally {
      setCarregando(false);
    }
  }, [slug, ehFuncionario, escopo]);

  useEffect(() => { carregar(); }, [carregar]);

  const voltar = () => navigate(`/torcida/${slug}`);
  const ehGlobal = ehFuncionario && escopo === 'global';
  // Prêmios são só por loja — não exibir no ranking geral/empresa (aba "Geral")
  const premiosCard = dados?.loja?.premios;
  const temPremios = !ehGlobal && premiosCard && (premiosCard.primeiro || premiosCard.segundo || premiosCard.terceiro);

  return (
    <div className="torc-app">
      <header className="torc-header">
        <div className="torc-deco" style={{ fontSize: '2.4rem' }}>🏆</div>
        <p className="torc-tagline" style={{ marginTop: 6 }}>
          {ehGlobal ? 'Ranking Geral — Empresa' : (dados?.loja?.nome || 'Ranking da Torcida')}
          {ehFuncionario && !ehGlobal ? ' • Funcionários' : ''}
        </p>
      </header>

      <div className="torc-container">
        {/* Toggle loja / geral — só funcionário */}
        {ehFuncionario && (
          <div className="torc-seg" role="tablist">
            <button role="tab" aria-selected={escopo === 'loja'} className={`torc-seg-btn ${escopo === 'loja' ? 'ativo' : ''}`} onClick={() => setEscopo('loja')}>Minha loja</button>
            <button role="tab" aria-selected={escopo === 'global'} className={`torc-seg-btn ${escopo === 'global' ? 'ativo' : ''}`} onClick={() => setEscopo('global')}>Geral (empresa)</button>
          </div>
        )}

        {/* Prêmios — clientes veem os de cliente; funcionários os de funcionário */}
        {temPremios && (
          <div className="torc-card torc-card-amarelo">
            <h3 style={{ marginTop: 0, fontSize: '1.05rem' }}>{ehFuncionario ? '🏅 Prêmios dos funcionários' : '🎁 Prêmios dos clientes'}</h3>
            {premiosCard.primeiro && <p style={{ margin: '4px 0', fontWeight: 700 }}>🥇 1º lugar: {premiosCard.primeiro}</p>}
            {premiosCard.segundo && <p style={{ margin: '4px 0', fontWeight: 700 }}>🥈 2º lugar: {premiosCard.segundo}</p>}
            {premiosCard.terceiro && <p style={{ margin: '4px 0', fontWeight: 700 }}>🥉 3º lugar: {premiosCard.terceiro}</p>}
            <p className="torc-mini" style={{ marginBottom: 0, color: 'var(--torc-azul)' }}>Os 3 primeiros da loja levam!</p>
          </div>
        )}

        <div className="torc-card">
          <h3 style={{ marginTop: 0, color: 'var(--torc-azul)', fontSize: '1.05rem' }}>
            {ehGlobal ? 'Ranking Geral dos Funcionários' : ehFuncionario ? 'Ranking dos Funcionários' : 'Ranking dos Clientes'}
          </h3>
          {carregando ? (
            <div style={{ textAlign: 'center', padding: 20 }}><div className="torc-spinner" style={{ borderColor: '#ddd', borderTopColor: 'var(--torc-verde)' }} /></div>
          ) : erro ? (
            <div className="torc-erro">{erro}</div>
          ) : !dados?.ranking?.length ? (
            <p className="torc-mini" style={{ textAlign: 'center' }}>Ainda não há participantes neste ranking. Seja o primeiro a palpitar! ⚽</p>
          ) : (
            dados.ranking.map((r) => (
              <div className="torc-rank-item" key={r.participante_id}>
                <div className={`torc-rank-pos ${r.posicao === 1 ? 'top1' : r.posicao === 2 ? 'top2' : r.posicao === 3 ? 'top3' : ''}`}>
                  {r.posicao}º
                </div>
                <div className="torc-rank-nome">
                  {r.nome_exibicao}
                  {ehGlobal && r.loja && <div style={{ fontSize: '0.72rem', color: '#778', fontWeight: 400 }}>{r.loja}</div>}
                </div>
                <div className="torc-rank-pts">{r.total_pontos} pts</div>
              </div>
            ))
          )}
        </div>

        {/* Como funciona a pontuação */}
        <div className="torc-card">
          <h3 style={{ marginTop: 0, color: 'var(--torc-azul)', fontSize: '1.05rem' }}>📊 Como funciona a pontuação</h3>
          <div className="torc-pontos-linha"><span>🎯 Cravou o placar exato</span><strong>10 pts</strong></div>
          <div className="torc-pontos-linha"><span>✅ Acertou só o vencedor</span><strong>5 pts</strong></div>
          <div className="torc-pontos-linha"><span>🤝 Acertou só o empate</span><strong>3 pts</strong></div>
          <div className="torc-pontos-linha"><span>❌ Errou o resultado</span><strong>0 pts</strong></div>
          <p className="torc-mini" style={{ margin: '10px 0 0', fontStyle: 'italic' }}>Vale sempre a maior: cravar o placar dá 10, mesmo num empate (ex.: 3x3 = 10 pts).</p>
        </div>

        {/* Período da campanha */}
        <div className="torc-card" style={{ textAlign: 'center' }}>
          <p style={{ margin: '0 0 6px', fontWeight: 800, color: 'var(--torc-verde)', fontSize: '1.05rem' }}>📅 Campanha: 11/06 a 27/06</p>
          <p className="torc-mini" style={{ margin: 0 }}>
            Palpite na fase de grupos do torneio. Os vencedores de cada loja serão divulgados em <strong>28/06</strong> no Instagram da Centrão. 🏆🇧🇷
          </p>
        </div>

        <button className="torc-btn torc-btn-azul" onClick={voltar}>← Voltar pra palpitar</button>
      </div>
    </div>
  );
}
